import { observable } from 'mobx';
import { transaction } from '@src/slide/domain/slide-app';
import MultiplyModel from '@src/slide/domain/ui-model/element-modeles/multiply-model';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { getEditorInstanceByModel } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/utils';
import type { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import type { IParagraphStyle, ITabstop } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { ptToCm, cmToPt } from '@src/slide/infrastructure/module/independent/unit';
import { FONT_SCALE } from '@editor/src/page/index/components/editor/config';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { handleModelMenuEvent } from '@src/slide/application/service/menu-manager/menu-event';
import { getMenuValues } from '@src/slide/application/service/menu-manager/menu-values';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IParagraphManager, LineHeightType } from './paragraph-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { ITipsManager } from '@src/slide/application/service/tips-manager/core/tips-manager.interface';
import { IEditorInstanceService } from '../../editor-instance-service/editor-instance-service.interface';
import { isGroupModel, isShapeModel, isTableModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import {
    IAccessibilityManager,
} from '@src/slide/application/service/accessibility-manager/core/accessibility-manager.interface';
import { IUndoRedoService } from '@src/slide/application/service/undo-redo-service/undo-redo-service.interface';
import { ILinkManager } from '@src/slide/application/service/link-manager/core/link-manager.interface';
import {
    ITextEditorStateService,
} from '@src/slide/application/service/text-editor-state-service/text-editor-state-service.interface';
import EditingStateManager from '@src/slide/application/service/editing-state-manager';
import { IFontManager } from '@src/slide/content-viewer/service/font-manager/core/font-manager.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import {
    AlignType,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/constant';
import { PcEditLogicService } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/pc-edit-logic-service';

export interface IUpdateProps {
    alignmentType?: string;
    lineHeight?: number;
    lineHeightType: LineHeightType | '';
    marginLeft?: number;
    textIndent?: number;
    spaceBefore?: number;
    spaceAfter?: number;
    tabSize?: number;
    tabList?: ITabstop[];
}

type ParaPropsAlignType = IParagraphStyle['textAlign'];

export default class ParagraphManager implements IParagraphManager {
    @observable public alignmentType?: string;
    @observable public marginLeft?: number;
    @observable public textIndent?: number;
    @observable public spaceBefore?: number;
    @observable public spaceAfter?: number;
    @observable public lineHeight?: number;
    @observable public lineHeightType?: LineHeightType;
    @observable public tabList?: ITabstop[];
    @observable public tabSize?: number;

    private selectedArray: Array<IElementBaseModel>;
    private cachedTable: any|null;
    private isEditing: boolean;
    private ParagraphMoreDialog: any | null;
    private selectionService: ISelectionService | undefined;
    private userPriviledgeService: IUserPriviledgeService | undefined;
    private editorInstanceService: IEditorInstanceService | undefined;
    private tipsManager: ITipsManager | undefined;

    public constructor(
        private uiDataService: IUiDataService,
        private linkManager: ILinkManager,
        private fontManager: IFontManager,
    ) {
        import(/* webpackChunkName: "lazyComponents" */ './paragraph-more-dialog/index').then((module) => {
            this.ParagraphMoreDialog = module.default;
            this.ParagraphMoreDialog.setParagraphManager(this);
        })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
    }

    public setTipsManagersDelegate(tipsManager: ITipsManager): void {
        if (this.tipsManager) return;
        this.tipsManager = tipsManager;
    }


    public setSelectionServiceDelegate(selectionService: ISelectionService): void {
        if (this.selectionService) return;
        this.selectionService = selectionService;
    }

    public setUserPriviledgeServiceDelegate(userPriviledgeService: IUserPriviledgeService): void {
        if (this.userPriviledgeService) return;
        this.userPriviledgeService = userPriviledgeService;
    }

    public setEditorInstanceServiceDelegate(editorInstanceService: IEditorInstanceService): void {
        if (this.editorInstanceService) return;
        this.editorInstanceService = editorInstanceService;
    }

    public showParagraph(config): void {
        this.cacheSelectedElements();
        this.cacheTable();
        this.cacheSelection();
        this.getSelectedStyle();
        this.ParagraphMoreDialog?.show({
            userPriviledgeService: this.userPriviledgeService,
            tipsManager: this.tipsManager,
            ...config,
            editorServices: {
                uiDataService: this.uiDataService,
                linkManager: this.linkManager,
                fontManager: this.fontManager,
                logicService: new PcEditLogicService(),
            },
        });
        // 如果是多选状态，canvas状态也清除选中元素
        if ((this.selectionService?.selectedElements.selectedArray?.length ?? 0) > 1) {
            this.clearSelection();
        }
    }

    public hideParagraph(): void {
        const needResume = !this.elementNotExist();
        if (needResume) {
            this.resumeSelectedElements();
            this.resumeTable();
            this.resetSelectedStyle();
        }
        this.ParagraphMoreDialog?.remove();
        this.cachedTable = null;
        this.isEditing = false;
    }

    // eslint-disable-next-line complexity
    public updateUI(updateProps: IUpdateProps): void | Promise<void> {
        if (Object.entries(updateProps).length === 0) {
            return;
        }
        if (this.elementNotExist()) return; // 有元素不存在的话就别搞了...

        this.resumeSelectedElements();
        this.resumeTable();

        const paraProps = this.convertUpdatePropsToParagraphProps(updateProps);
        if (this.isEditing) {
            const [model] = this.selectedArray;
            if (isTableModel(model)) {
                this.handleMenuEvent(model, ActionType.UPDATE_PARAGRAPH_PROPS, paraProps);
                return;
            }
            const editorInstance = getEditorInstanceByModel(model);
            if (!editorInstance) return;
            editorInstance.operations('paragraph-props', paraProps);
        } else {
            const selectedArray = this.selectionService?.selectedElements.selectedArray;
            if (!selectedArray) return;
            transaction(() => {
                selectedArray.forEach((element) => {
                    this.updateElementParagraphProps(element, paraProps, updateProps);
                });
            });
        }
    }

    public updateUIWithModel(updateProps: IUpdateProps, model): void | Promise<void> {
        if (Object.entries(updateProps).length === 0) {
            return;
        }

        const paraProps = this.convertUpdatePropsToParagraphProps(updateProps);
        const selectedArray = this.selectionService?.selectedElements.selectedArray;
        if (!selectedArray) return;
        transaction(() => {
            selectedArray.forEach((element) => {
                this.updateElementParagraphProps(element, paraProps, updateProps);
            });
        });
    }

    // eslint-disable-next-line complexity
    public getSelectedStyle = () => {
        let itemValues: any;
        if (this.isEditing) {
            const [model] = this.selectedArray;
            itemValues = getMenuValues(model);
        } else {
            if (!this.selectionService?.selectedElements.selectedArray) return;

            // 只考虑单选且只有表格到情况，如果是多选同multiply-model
            let [model] = this.selectionService?.selectedElements.selectedArray as any[];
            if (!(this.selectionService
                ?.selectedElements.selectedArray.length === 1 && isTableModel(model))) {
                model = new MultiplyModel({
                    selectedModels: this.selectionService?.selectedElements.selectedArray,
                    selectionService: this.selectionService,
                });
            }
            itemValues = getMenuValues(model);
        }
        if (!itemValues) {
            return;
        }
        this.setLineHeight(itemValues);
        this.alignmentType = itemValues[ActionType.HORIZONTAL_ALIGN];
        this.marginLeft = ptToCm(parseFloat(itemValues[ActionType.MARGIN_LEFT] ?? 0), 2) / FONT_SCALE;
        this.textIndent = ptToCm(parseFloat(itemValues[ActionType.TEXT_INDENT] ?? 0), 2) / FONT_SCALE;
        this.spaceBefore = parseFloat(itemValues[ActionType.SPACE_BEFORE] ?? 0) / FONT_SCALE;
        this.spaceAfter = parseFloat(itemValues[ActionType.SPACE_AFTER] ?? 0) / FONT_SCALE;
        try {
            this.tabList = itemValues[ActionType.TAB_LIST].map(tabstop => ({
                ...tabstop,
                position: tabstop.position / FONT_SCALE,
            }));
        } catch (e) {
            this.tabList = [];
        }
        this.tabSize = parseFloat(itemValues[ActionType.TAB_SIZE]) / FONT_SCALE;
    };

    // eslint-disable-next-line complexity
    private convertUpdatePropsToParagraphProps(updateProps: IUpdateProps): IParagraphStyle {
        const {
            alignmentType,
            lineHeight,
            lineHeightType: rawLineHeightType,
            marginLeft,
            textIndent,
            spaceBefore,
            spaceAfter,
            tabList,
            tabSize,
        } = updateProps;
        const lineHeightType = (rawLineHeightType === LineHeightType.Multi && 'Multi') || 'Fixed';
        let paraProps: IParagraphStyle = {};
        if (lineHeight !== undefined) {
            if (lineHeightType === 'Multi') {
                paraProps = {
                    ...paraProps,
                    lineHeight,
                    PPTLineHeight: lineHeight,
                    lineHeightIs: 'percent',
                };
            } else {
                paraProps = {
                    ...paraProps,
                    lineHeight,
                    lineHeightIs: 'absolute',
                    PPTLineHeight: undefined,
                };
            }
        }
        if (marginLeft !== undefined) {
            paraProps.marginLeft = cmToPt(marginLeft, 2);
        }
        if (textIndent !== undefined) {
            paraProps.textIndent = cmToPt(textIndent, 2);
        }
        if (spaceBefore !== undefined) {
            paraProps.paddingTop = spaceBefore;
            paraProps.paddingTopIs = 'absolute';
        }
        if (spaceAfter !== undefined) {
            paraProps.paddingBottom = spaceAfter;
            paraProps.paddingBottomIs = 'absolute';
        }
        if (tabList !== undefined) {
            paraProps.tabList = tabList;
        }
        if (tabSize !== undefined) {
            paraProps.tabSize = tabSize;
        }
        if (alignmentType !== undefined) {
            paraProps.textAlign = this.getTextAlign(alignmentType);
        }
        return paraProps;
    }

    private handleMenuEvent(model: IBaseModel, id: string | null, updateVal: any) {
        if (updateVal !== null && updateVal !== undefined && id !== null) {
            handleModelMenuEvent(model, {
                menuType: MenuType.TOOLBAR,
                actionType: id,
                values: updateVal,
            });
        }
    }

    private elementNotExist() {
        return !this.selectedArray.find(model => !!this.selectionService
            ?.currentSlide?.children.find((element) => {
                if (!element) {
                    return false;
                }
                const checkHasElement = (ele) => {
                    if (ele === model) {
                        return true;
                    }
                    if (isShapeModel(ele)) {
                        return ele === model;
                    }
                    if (isGroupModel(ele)) {
                        return ele.children.some(child => checkHasElement(child));
                    }
                };
                return checkHasElement(element);
            }));
    }

    private clearSelection = () => {
        this.selectionService?.selectedElements.clear();
    };

    private resetSelectedStyle = () => {
        this.lineHeight = undefined;
        this.lineHeightType = undefined;
        this.alignmentType = undefined;
        this.marginLeft = undefined;
        this.textIndent = undefined;
        this.spaceBefore = undefined;
        this.spaceAfter = undefined;
    };

    private updateElementParagraphProps(
        element: IBaseModel | null | undefined,
        paraProps: IParagraphStyle,
        updateProps: IUpdateProps,
    ): void {
        if (!element) {
            return;
        }
        if (isGroupModel(element)) {
            element.children.forEach(ele => this.updateElementParagraphProps(ele, paraProps, updateProps));
        } else {
            this.handleMenuEvent(element, ActionType.UPDATE_PARAGRAPH_PROPS, paraProps);
        }
    }

    private cacheSelectedElements = () => {
        const selectedArray = this.selectionService?.selectedElements.selectedArray;
        this.selectedArray = selectedArray?.concat() ?? [];
    };

    private cacheTable = () => {
        const selectedArray = this.selectionService?.selectedElements.selectedArray;
        if ((selectedArray?.length ?? 0) > 1) return; // 选中多个表格不处理
        const [model] = selectedArray as any[];
        if (!isTableModel(model)) return;
        this.cachedTable = {
            ...(model.selectedRange ?? {}),
            showSelection: model.showSelection,
        };
    };

    private cacheSelection = () => {
        if (!this.editorInstanceService?.checkEditorIsEditing()) {
            this.isEditing = false;
            return;
        }
        this.isEditing = true;
    };

    private resumeSelectedElements = () => {
        if (!this.selectionService?.selectedElements.selectedArray) {
            this.selectionService?.selectedElements.add(...this.selectedArray);
        }
    };

    private resumeTable = () => {
        if (!this.cachedTable) return;
        if (!this.selectionService?.selectedElements.selectedArray) return;
        if (!this.selectionService?.selectedElements.selectedArray.length) return;
        const [model] = this.selectionService?.selectedElements.selectedArray as any[];
        if (!isTableModel(model)) return;

        if (this.isEditing) {
            const row = this.cachedTable.rowStart;
            const col = this.cachedTable.colStart;
            const editorModel = model.tableItems[row][col];
            editorModel && (editorModel.isEditing = true);
        }

        model.showSelection = this.cachedTable.showSelection;
        model.afterSelection(
            this.cachedTable.rowStart,
            this.cachedTable.colStart,
            this.cachedTable.rowEnd,
            this.cachedTable.colEnd,
        );
    };

    private setLineHeight = (itemValues: any) => {
        const lineHeightIs = itemValues[ActionType.LINE_HEIGHT_IS];
        if (lineHeightIs === 'absolute') {
            const lineHeightAbsolute = parseFloat(itemValues[ActionType.ROW_SPACE_ABSOLUTE]);
            if (isNaN(lineHeightAbsolute)) {
                return;
            }
            this.lineHeight = lineHeightAbsolute / 2;
            this.lineHeightType = LineHeightType.Fixed;
        } else if (lineHeightIs === 'percent') {
            const lineHeight = parseFloat(itemValues[ActionType.ROW_SPACE]);
            if (isNaN(lineHeight)) {
                return;
            }
            this.lineHeight = lineHeight;
            this.lineHeightType = LineHeightType.Multi;
        }
    };

    private getTextAlign(alignmentType: string): ParaPropsAlignType {
        const alignMap = {
            [AlignType.HORIZONTAL_LEFT]: 'left',
            [AlignType.HORIZONTAL_CENTER]: 'center',
            [AlignType.HORIZONTAL_RIGHT]: 'right',
            [AlignType.HORIZONTAL_JUSTIFY]: 'justify',
            [AlignType.HORIZONTAL_DISTRIBUTE]: 'distribute',
        };
        return alignMap[alignmentType] as ParaPropsAlignType;
    }
}
